import React, { useCallback } from 'react';

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import type { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import IntervalTextInput from '../Inputs/IntervalTextInput';
import SelectDayCalendar from '../Inputs/SelectDayCalendar';
import SelectDayWeek from '../Inputs/SelectDayWeek/SelectDayWeek';
import SelectPosition from '../Inputs/SelectPosition/SelectPosition';
import { MonthBy, type AllRepeatDetails } from '../Repeat.types';

interface RepeatMonthlyProps {
  value: AllRepeatDetails;
  onChange: (value: AllRepeatDetails) => void;
  radioValue: MonthBy;
  setRadioValue: (value: MonthBy) => void;
  inputSize: 'small' | 'medium';
  inputVariant: TextFieldProps['variant'];
}

const RepeatMonthly = ({
  value,
  onChange,
  radioValue,
  setRadioValue,
  inputSize = 'small',
  inputVariant,
}: RepeatMonthlyProps) => {
  const maxDaysInMonth = 31;
  const disabledOnBYSETPOS = radioValue === MonthBy.BYMONTHDAY;
  const disabledOnBYMONTHDAY = radioValue === MonthBy.BYSETPOS;

  // Dynamically calculate size or pass it as a prop
  const size = globalThis.window === undefined ? 400 : window.innerWidth;

  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const radioVal = e.target.value as MonthBy;
      setRadioValue(radioVal);
      if (radioVal === MonthBy.BYMONTHDAY) {
        onChange({
          ...value,
          bySetPos: [],
          byDay: [],
          byMonth: [],
        });
      } else {
        onChange({ ...value, byMonthDay: [], byMonth: [] });
      }
    },
    [onChange, value, setRadioValue],
  );

  const handleOnDayChange = useCallback(
    (allRepeatDetails: AllRepeatDetails) => {
      onChange({ ...allRepeatDetails });
      if (radioValue !== MonthBy.BYMONTHDAY) {
        setRadioValue(MonthBy.BYMONTHDAY);
      }
    },
    [onChange, radioValue, setRadioValue],
  );

  const handleOnTheChange = useCallback(
    (allRepeatDetails: AllRepeatDetails) => {
      onChange({ ...allRepeatDetails });
      if (radioValue !== MonthBy.BYSETPOS) {
        setRadioValue(MonthBy.BYSETPOS);
      }
    },
    [onChange, radioValue, setRadioValue],
  );

  return (
    <Stack direction="column" spacing={2} sx={{ alignItems: 'flex-start', width: '100%' }}>
      <IntervalTextInput
        value={value}
        onChange={onChange}
        unit="month"
        pluralizeUnit
        inputSize={inputSize}
        inputVariant={inputVariant}
      />
      <RadioGroup name="monthly" value={radioValue} onChange={handleRadioChange} sx={{ width: '100%' }}>
        <Stack direction="column" spacing={2} sx={{ alignItems: 'flex-start', width: '100%' }}>
          {/* ON DAY SECTION */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <FormControlLabel
              value={MonthBy.BYMONTHDAY}
              control={<Radio />}
              label={
                <Typography
                  sx={{
                    color: disabledOnBYMONTHDAY ? 'text.disabled' : 'text.primary',
                    paddingLeft: 2,
                    fontSize: inputSize,
                  }}
                >
                  On Day
                </Typography>
              }
              sx={{ minWidth: 120, marginRight: 2 }}
            />
            <SelectDayCalendar
              value={value}
              onChange={handleOnDayChange}
              maxDaysInMonth={maxDaysInMonth}
              disabled={disabledOnBYMONTHDAY}
              inputSize={inputSize}
              inputVariant={inputVariant}
            />
          </Box>
          {/* ON THE SECTION */}
          <Stack
            direction={size < 301 ? 'column' : 'row'}
            spacing={4}
            sx={{ width: '100%', alignItems: size < 301 ? 'flex-start' : 'center' }}
          >
            <Box sx={{ minWidth: 120, marginRight: 2 }}>
              <FormControlLabel
                value={MonthBy.BYSETPOS}
                control={<Radio />}
                label={
                  <Typography
                    sx={{
                      color: disabledOnBYSETPOS ? 'text.disabled' : 'text.primary',
                      paddingLeft: 2,
                      fontSize: inputSize,
                    }}
                  >
                    On The
                  </Typography>
                }
              />
            </Box>
            <Box
              sx={{
                minWidth: 120,
                marginX: { xs: 0, sm: 2 },
                marginY: { xs: 2, sm: 0 },
                width: size < 301 ? '100%' : 'auto',
              }}
            >
              <SelectPosition
                value={value}
                onChange={handleOnTheChange}
                disabled={disabledOnBYSETPOS}
                inputSize={inputSize}
                inputVariant={inputVariant}
              />
            </Box>
            <Box
              sx={{
                minWidth: 120,
                marginX: { xs: 0, sm: 2 },
                width: size < 301 ? '100%' : 'auto',
              }}
            >
              <SelectDayWeek
                value={value}
                onChange={handleOnTheChange}
                disabled={disabledOnBYSETPOS}
                inputSize={inputSize}
                inputVariant={inputVariant}
              />
            </Box>
          </Stack>
        </Stack>
      </RadioGroup>
    </Stack>
  );
};
export default RepeatMonthly;
